import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Download, Mic, Star, Scissors, Edit3, Menu, Settings, ChevronDown, ChevronUp, Share2, BarChart2 } from '@geist-ui/icons';
import { AuroraBackground } from '../components/ui/aurora-background'; // AuroraBackgroundDemo yerine AuroraBackground
import HowItWorksDemo from '../components/HowItWorksDemo';

// Logoyu import etmeyi unutma, eğer /assets klasörü src içindeyse:
// import clipoidLogo from '../assets/clipoid_logo.png';
// Eğer public klasöründeyse, doğrudan path kullanılabilir.
// const clipoidLogo = '/assets/clipoid_logo.png'; // Artık kullanılmıyor, AppHeader'da genel logo var.

const LandingPage: React.FC = () => {
  const [isBetaInfoVisible, setIsBetaInfoVisible] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const texts = [
    {
      title: "Automate",
      subtitle: "Your Stream Highlights.",
      description: "Tired of sifting through hours of footage? Clipoid helps you find your most captivating stream moments and empowers you to quickly create viral short-form videos for platforms like TikTok and YouTube Shorts. Make your content stand out and focus on creating – we'll handle the clips! Optional transcription services can further enhance your reach.",
      isLarge: true,
    },
    {
      fullText: "Your first 1000 followers start here.",
      description: "Unlock exponential growth and reach your first milestone. Clipoid helps you create engaging content that attracts new viewers and builds a loyal community, propelling you towards 1000 followers and beyond.",
      isLarge: false,
    },
    {
      fullText: "Editing? Handled. Growth? Accelerated.",
      description: "Say goodbye to tedious editing and hello to explosive growth. Clipoid's AI handles the heavy lifting of finding and refining your highlights, so you can focus on creating content that captivates and converts viewers into dedicated fans.",
      isLarge: false,
    },
    {
      fullText: "From Streamers, to Streamers.",
      description: "Built by streamers who understand your grind. We've poured our passion and expertise into creating a platform that solves real-world content challenges, empowering you with the tools we wish we had from day one.",
      isLarge: false,
    }
  ];

  const toggleBetaInfo = () => {
    setIsBetaInfoVisible(!isBetaInfoVisible);
  };

  const toggleAboutSection = () => {
    setIsAboutOpen(!isAboutOpen);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setIsFading(true);
      setTimeout(() => {
        setCurrentTextIndex((prevIndex) => (prevIndex + 1) % texts.length);
        setIsFading(false);
      }, 1000); // Fade out süresi 1 saniyeye çıkarıldı
    }, 8000); // 7 saniye bekleme + 1 saniye fade out (Toplam süre aynı)

    return () => clearInterval(interval);
  }, [texts.length]);

  useEffect(() => {
    // FAQ Accordion Logic
    const faqItems = document.querySelectorAll('.faq-item');
    const listeners: (() => void)[] = [];
    faqItems.forEach(item => {
      const button = item.querySelector('.faq-question') as HTMLButtonElement | null;
      const answer = item.querySelector('.faq-answer') as HTMLElement | null;
      if (button && answer) {
        const clickHandler = () => {
          answer.classList.toggle('hidden');
        };
        button.addEventListener('click', clickHandler);
        listeners.push(() => button.removeEventListener('click', clickHandler));
      }
    });

    // Feature Card Glow on Scroll for Mobile
    const featureCards = document.querySelectorAll('.feature-card');
    const isMobile = () => window.innerWidth < 768; // md breakpoint

    const observerCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && isMobile()) {
          const card = entry.target as HTMLElement;
          const glowShadowClass = card.dataset['glowShadow'] || ''; // e.g., shadow-[0_0_20px_3px_#22C55E40]
          const glowBorderClass = card.dataset['glowBorder'] || ''; // e.g., border-green-500/70

          // Apply glow effects
          if (glowShadowClass) card.classList.add(glowShadowClass.replace('hover:', '')); // Remove hover: prefix if present
          if (glowBorderClass) card.classList.add(glowBorderClass.replace('hover:', ''));

          // Remove glow effects after a delay
          setTimeout(() => {
            if (glowShadowClass) card.classList.remove(glowShadowClass.replace('hover:', ''));
            if (glowBorderClass) card.classList.remove(glowBorderClass.replace('hover:', ''));
          }, 1500); // Glow duration

        // observer.unobserve(card); // Observe only once - Removed to allow re-triggering
      }
    });
    };

    if (featureCards.length > 0) {
      const observerOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1 // Trigger when 10% of the card is visible
      };
      const featureObserver = new IntersectionObserver(observerCallback, observerOptions);
      featureCards.forEach(card => featureObserver.observe(card));

      // Cleanup observer on component unmount
      return () => {
        featureCards.forEach(card => featureObserver.unobserve(card));
        listeners.forEach(remove => remove());
      };
    }
    return () => {
      listeners.forEach(remove => remove());
    };
  }, []);

  // Handle clip editor logout detection
  useEffect(() => {
    const clipeditorLogout = searchParams.get('clipeditor_logout');
    const returnUrl = searchParams.get('returnUrl');
    
    if (clipeditorLogout === 'true') {
      console.log('[LandingPage] Detected logout from clip editor');
      
      // Show a message to the user
      const message = 'You have been logged out. Please log in again to continue using the clip editor.';
      
      // Clean up URL parameters
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('clipeditor_logout');
      
      // If returnUrl exists, clean it and store it for after login
      if (returnUrl) {
        try {
          // Decode and clean the return URL to prevent infinite loops
          const decodedReturnUrl = decodeURIComponent(returnUrl);
          const cleanReturnUrl = new URL(decodedReturnUrl);
          cleanReturnUrl.searchParams.delete('clipeditor_logout');
          cleanReturnUrl.searchParams.delete('returnUrl');
          
          localStorage.setItem('clipeditor_return_url', cleanReturnUrl.toString());
        } catch (error) {
          console.warn('[LandingPage] Failed to clean return URL:', error);
          // Fallback: store the original URL if cleaning fails
          localStorage.setItem('clipeditor_return_url', returnUrl);
        }
        newUrl.searchParams.delete('returnUrl');
      }
      
      // Update URL without the parameters
      window.history.replaceState({}, '', newUrl.toString());
      
      // Show alert or notification (you might want to replace this with a better UI)
      setTimeout(() => {
        alert(message);
      }, 100);
      
      // Redirect to login page after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    }
  }, [searchParams, navigate]);

  return (
    <AuroraBackground>
      {/* Hero Section */}
      <header className="container mx-auto px-6 pt-24 pb-16 md:pt-28 md:pb-20 text-center relative flex flex-col justify-start items-center min-h-[400px] md:min-h-[430px]"> {/* pt-16'dan pt-24'e artırıldı sticky header için */}
        <div className={`transition-opacity duration-1000 ease-in-out w-full flex flex-col items-center justify-center min-h-[80px] md:min-h-[100px] ${isFading ? 'opacity-0' : 'opacity-100'}`}> {/* isFading class'ı eklendi */}
          {texts[currentTextIndex].isLarge ? (
            <h1 className="text-5xl md:text-6xl font-bold"> {/* mb-6 kaldırıldı */}
              <span className="bg-gradient-to-r from-primary to-[hsl(207_90%_70%)] bg-clip-text text-transparent drop-shadow-[0_0_8px_hsl(var(--primary)/0.5)]">{texts[currentTextIndex].title}</span>
              {' '}
              <span className="bg-gradient-to-r from-foreground to-[hsl(var(--foreground)/0.7)] bg-clip-text text-transparent drop-shadow-[0_0_5px_hsl(var(--foreground)/0.3)]">{texts[currentTextIndex].subtitle}</span>
            </h1>
          ) : (
            <p className="text-4xl md:text-5xl font-semibold text-foreground max-w-3xl mx-auto"> {/* mb-6 kaldırıldı */}
              {texts[currentTextIndex].fullText}
            </p>
          )}
        </div>
        {/* Açıklama metni, her zaman gösterilecek ve dinamik olacak */}
        <div className={`transition-opacity duration-1000 ease-in-out mt-6 min-h-[100px] md:min-h-[120px] ${isFading ? 'opacity-0' : 'opacity-100'}`}> {/* isFading class'ı eklendi */}
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {texts[currentTextIndex].description}
          </p>
        </div>
        <div className="mt-10 flex flex-col items-center space-y-4 sm:flex-row sm:space-y-0 sm:space-x-6 sm:justify-center"> {/* mt-10 olarak ayarlandı */}
          <a
            href="#features"
            className="glass-button bg-gradient-to-r from-primary/80 to-blue-600/80 text-primary-foreground hover:from-primary hover:to-blue-600 font-semibold py-3 px-8 rounded-xl text-lg shadow-2xl hover:shadow-[0_0_30px_5px_hsl(var(--primary)/0.8)] hover:scale-105 transition-all duration-300 ease-in-out w-full sm:w-auto text-center backdrop-blur-md border border-white/10 relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700 ease-out"></div>
            <span className="relative z-10">Discover Features</span>
          </a>
          <div className="relative flex items-start space-x-2"> {/* items-start to align Get Started Free and Beta button container */}
            <div className="relative group"> {/* Added group for hover effect on desktop */}
              <Link
                to="/register"
                className="glass-button border-2 border-primary/50 text-primary hover:bg-primary/10 hover:text-primary hover:border-primary font-semibold py-3 px-8 rounded-xl text-lg hover:shadow-[0_0_25px_3px_hsl(var(--primary)/0.9)] hover:scale-105 transition-all duration-300 ease-in-out w-full sm:w-auto text-center block backdrop-blur-md" // Added block
              >
                Get Started Free
              </Link>
              {/* BETA Button - Positioned relative to the Link above */}
              <button
                onClick={toggleBetaInfo}
                className="absolute -top-2 -right-3 transform translate-x-1/2 -translate-y-1/2 glass-button bg-gradient-to-r from-pink-600/80 to-pink-500/80 text-white text-xs font-bold px-2.5 py-1 rounded-full cursor-pointer focus:outline-none z-20 shadow-xl hover:from-pink-600 hover:to-pink-500 hover:scale-110 transition-all duration-300 backdrop-blur-md border border-pink-400/30"
                aria-expanded={isBetaInfoVisible}
                aria-controls="beta-info-tooltip"
              >
                BETA
              </button>
              {/* BETA Tooltip - Conditional rendering for mobile (click) and desktop (hover) */}
              {/* Mobile: visible based on isBetaInfoVisible state */}
              <div
                id="beta-info-tooltip-mobile"
                className={`md:hidden absolute top-full mt-2 w-60 p-3 glass-card bg-card/80 text-card-foreground text-sm rounded-xl shadow-2xl z-10 left-1/2 transform -translate-x-1/2 border border-pink-500/30 backdrop-blur-lg ${isBetaInfoVisible ? 'block' : 'hidden'}`}
                role="tooltip"
              >
                Clipoid is currently in active development (Beta). Some features might be experimental. We appreciate your feedback!
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 border-b-pink-500/30"></div>
              </div>
              {/* Desktop: visible on hover of the parent 'group' */}
              <div
                id="beta-info-tooltip-desktop"
                className="hidden md:group-hover:block absolute left-[calc(100%+0.5rem)] top-1/2 -translate-y-1/2 w-72 p-3 glass-card bg-card/80 text-card-foreground text-sm rounded-xl shadow-2xl z-10 border border-pink-500/30 hover:shadow-[0_0_20px_3px_#EC489950] backdrop-blur-lg" // #EC4899 is pink-600
                role="tooltip"
              >
                 Clipoid is currently in active development (Beta). This means some features might be experimental or evolve as we gather your valuable feedback. We're working hard to deliver the best experience!
                <div className="absolute right-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-pink-500/30"></div> {/* Arrow pointing left */}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section id="features" className="py-20 text-foreground relative"> {/* z-20 kaldırıldı */}
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4 text-primary">What Can Clipoid Do For You?</h2>
          <p className="text-lg text-muted-foreground text-center mb-16 max-w-xl mx-auto">
            From VOD to viral, Clipoid streamlines your content creation workflow, letting you focus on what you do best: creating amazing content.
          </p>
          
          {/* How It Works Demo Section */}
          <HowItWorksDemo />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-20">
            {/* Feature 1: VOD Downloading */}
            <div
              className="feature-card glass-card bg-card/50 p-8 rounded-xl shadow-2xl md:hover:shadow-[0_0_30px_8px_hsl(var(--primary)/0.5)] transition-all duration-300 border border-white/10 md:hover:border-primary/60 backdrop-blur-lg"
              data-glow-shadow="shadow-[0_0_30px_8px_hsl(var(--primary)/0.5)]"
              data-glow-border="border-primary/60"
            >
              <div className="text-primary mb-4">
                <Download size={48} />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-card-foreground">Automatic VOD Fetching</h3>
              <p className="text-muted-foreground">
                Simply link your Twitch account, and Clipoid gets to work, automatically securing your past broadcasts (VODs) so they're ready for their highlight reel. No more manual downloads!
              </p>
            </div>
            {/* Feature 2: Transcription */}
            <div
              className="feature-card glass-card bg-card/50 p-8 rounded-xl shadow-2xl md:hover:shadow-[0_0_30px_8px_#22C55E60] transition-all duration-300 border border-white/10 md:hover:border-green-500/60 backdrop-blur-lg"
              data-glow-shadow="shadow-[0_0_30px_8px_#22C55E60]"
              data-glow-border="border-green-500/60"
            >
              <div className="text-green-500 mb-4">
                <Mic size={48} />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-card-foreground">Accurate Stream Transcription</h3>
              <p className="text-muted-foreground">
                Clipoid accurately transcribes your stream's audio, turning spoken words into searchable text. This makes your content more accessible and simplifies subtitle creation.
              </p>
            </div>
            {/* Feature 3: Highlight Detection */}
            <div
              className="feature-card glass-card bg-card/50 p-8 rounded-xl shadow-2xl md:hover:shadow-[0_0_30px_8px_#EAB30860] transition-all duration-300 border border-white/10 md:hover:border-yellow-500/60 backdrop-blur-lg"
              data-glow-shadow="shadow-[0_0_30px_8px_#EAB30860]"
              data-glow-border="border-yellow-500/60"
            >
              <div className="text-yellow-500 mb-4">
                <Star size={48} />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-card-foreground">Intelligent Moment Discovery</h3>
              <p className="text-muted-foreground">
                Clipoid helps you discover the most engaging segments from your streams, making it easier to find those must-see moments. Spend less time searching and more time creating!
              </p>
            </div>
            {/* Feature 4: Video Cutting */}
            <div
              className="feature-card glass-card bg-card/50 p-8 rounded-xl shadow-2xl md:hover:shadow-[0_0_30px_8px_#EC489960] transition-all duration-300 border border-white/10 md:hover:border-pink-500/60 backdrop-blur-lg"
              data-glow-shadow="shadow-[0_0_30px_8px_#EC489960]"
              data-glow-border="border-pink-500/60"
            >
              <div className="text-pink-500 mb-4">
                <Scissors size={48} />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-card-foreground">Effortless Clip Creation</h3>
              <p className="text-muted-foreground">
                Transform suggested highlights into polished, shareable clips with just a few clicks. Export in formats perfect for TikTok, YouTube Shorts, Instagram Reels, and more.
              </p>
            </div>
            {/* Feature 5: Intuitive Clip Editor */}
            <div
              className="feature-card glass-card bg-card/50 p-8 rounded-xl shadow-2xl md:hover:shadow-[0_0_30px_8px_#A855F760] transition-all duration-300 border border-white/10 md:hover:border-purple-500/60 backdrop-blur-lg"
              data-glow-shadow="shadow-[0_0_30px_8px_#A855F760]"
              data-glow-border="border-purple-500/60"
            >
              <div className="text-purple-500 mb-4">
                <Edit3 size={48} />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-card-foreground">Your Personal Video Editor</h3>
              <p className="text-muted-foreground">
                Our built-in editor is designed for speed and ease. Add text overlays, choose engaging transitions, and fine-tune your clips to perfection without needing complex software.
              </p>
            </div>
            {/* Feature 6: Queue Management */}
            <div
              className="feature-card glass-card bg-card/50 p-8 rounded-xl shadow-2xl md:hover:shadow-[0_0_30px_8px_#F9731660] transition-all duration-300 border border-white/10 md:hover:border-orange-500/60 backdrop-blur-lg"
              data-glow-shadow="shadow-[0_0_30px_8px_#F9731660]"
              data-glow-border="border-orange-500/60"
            >
              <div className="text-orange-500 mb-4">
                <Menu size={48} />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-card-foreground">Robust Processing Queue</h3>
              <p className="text-muted-foreground">
                Got a backlog of VODs? No problem. Submit them all! Clipoid’s powerful queue system processes your content efficiently in the background and notifies you when your clips are ready.
              </p>
            </div>
            {/* Feature 7: Customizable Output */}
            <div
              className="feature-card glass-card bg-card/50 p-8 rounded-xl shadow-2xl md:hover:shadow-[0_0_30px_8px_#06B6D460] transition-all duration-300 border border-white/10 md:hover:border-cyan-500/60 backdrop-blur-lg"
              data-glow-shadow="shadow-[0_0_30px_8px_#06B6D460]"
              data-glow-border="border-cyan-500/60"
            >
              <div className="text-cyan-500 mb-4">
                <Settings size={48} />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-card-foreground">Customizable Settings</h3>
              <p className="text-muted-foreground">
                Tailor Clipoid to your workflow. Adjust transcription settings, choose your preferred processing options, and access advanced customization (on eligible plans) for truly unique content.
              </p>
            </div>
            {/* Feature 8: Multi-Platform Export */}
            <div
              className="feature-card glass-card bg-card/50 p-8 rounded-xl shadow-2xl md:hover:shadow-[0_0_30px_8px_#D946EF60] transition-all duration-300 border border-white/10 md:hover:border-fuchsia-500/60 backdrop-blur-lg"
              data-glow-shadow="shadow-[0_0_30px_8px_#D946EF60]"
              data-glow-border="border-fuchsia-500/60"
            >
              <div className="text-fuchsia-500 mb-4">
                <Share2 size={48} />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-card-foreground">Multi-Platform Export</h3>
              <p className="text-muted-foreground">
                Directly export the clips you create to popular platforms like TikTok, YouTube Shorts, and Instagram Reels with a single click, or download them easily.
              </p>
            </div>
            {/* Feature 9: Advanced Analytics */}
            <div
              className="feature-card glass-card bg-card/50 p-8 rounded-xl shadow-2xl md:hover:shadow-[0_0_30px_8px_#84CC1660] transition-all duration-300 border border-white/10 md:hover:border-lime-500/60 backdrop-blur-lg"
              data-glow-shadow="shadow-[0_0_30px_8px_#84CC1660]"
              data-glow-border="border-lime-500/60"
            >
              <div className="text-lime-500 mb-4">
                <BarChart2 size={48} />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-card-foreground">Advanced Analytics</h3>
              <p className="text-muted-foreground">
                Track which of your clips get more engagement, their watch times, and more to improve your content strategy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Slogan Section 2 */}
      <section className="py-16 text-primary-foreground relative"> {/* z-20 kaldırıldı */}
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold">
            Clip Like an Octopus — Clipoid.
          </h2>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 text-foreground relative"> {/* z-20 kaldırıldı */}
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16 text-primary">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {/* FAQ Item 1 */}
            <div className="glass-card bg-card/50 rounded-xl shadow-xl faq-item border border-white/10 backdrop-blur-lg hover:border-white/20 transition-all duration-300">
              <button className="w-full text-left p-6 focus:outline-none faq-question">
                <h3 className="text-xl font-semibold text-card-foreground">What exactly is Clipoid?</h3>
              </button>
              <div className="px-6 pb-6 text-muted-foreground hidden faq-answer">
                <p>
                  Clipoid is your co-pilot for content creation! It helps you process your Twitch VODs to find key moments and quickly create engaging short-form videos. We also offer accurate audio transcription to make your content more accessible and assist with subtitles. We streamline the tedious parts so you can focus on growing your community.
                </p>
              </div>
            </div>
            {/* FAQ Item 2 */}
            <div className="glass-card bg-card/50 rounded-xl shadow-xl faq-item border border-white/10 backdrop-blur-lg hover:border-white/20 transition-all duration-300">
              <button className="w-full text-left p-6 focus:outline-none faq-question">
                <h3 className="text-xl font-semibold text-card-foreground">Which platforms do you support?</h3>
              </button>
              <div className="px-6 pb-6 text-muted-foreground hidden faq-answer">
                <p>Currently, Clipoid is optimized for Twitch VODs. We're actively working on expanding support to other popular streaming and video platforms in the near future. Stay tuned!</p>
              </div>
            </div>
            {/* FAQ Item 3 */}
            <div className="glass-card bg-card/50 rounded-xl shadow-xl faq-item border border-white/10 backdrop-blur-lg hover:border-white/20 transition-all duration-300">
              <button className="w-full text-left p-6 focus:outline-none faq-question">
                <h3 className="text-xl font-semibold text-card-foreground">How are clips created?</h3>
              </button>
              <div className="px-6 pb-6 text-muted-foreground hidden faq-answer">
                <p>
                  Clipoid uses an intelligent system to identify potentially exciting or humorous moments from your streams. You'll receive suggestions, and our user-friendly editor allows you to easily trim, refine, and export these as shareable videos.
                </p>
              </div>
            </div>
            {/* FAQ Item 4 */}
            <div className="glass-card bg-card/50 rounded-xl shadow-xl faq-item border border-white/10 backdrop-blur-lg hover:border-white/20 transition-all duration-300">
              <button className="w-full text-left p-6 focus:outline-none faq-question">
                <h3 className="text-xl font-semibold text-card-foreground">How does pricing work?</h3>
              </button>
              <div className="px-6 pb-6 text-muted-foreground hidden faq-answer">
                <p>
                  We offer flexible subscription tiers designed to fit every creator's needs, from casual streamers to full-time content professionals. Each plan provides different levels of VOD processing and access to premium features. You can find all the details on our "Subscription" page once you sign up.
                </p>
              </div>
            </div>
            {/* FAQ Item 5 */}
            <div className="glass-card bg-card/50 rounded-xl shadow-xl faq-item border border-white/10 backdrop-blur-lg hover:border-white/20 transition-all duration-300">
              <button className="w-full text-left p-6 focus:outline-none faq-question">
                <h3 className="text-xl font-semibold text-card-foreground">Which type of content creators is Clipoid best suited for?</h3>
              </button>
              <div className="px-6 pb-6 text-muted-foreground hidden faq-answer">
                <p>
                  Clipoid is ideal for a wide range of Twitch streamers, from gamers and educational content creators to live talk show hosts and product reviewers. If you want to extract short, impactful moments from your VODs and share them on social media, Clipoid will save you valuable time.
                </p>
              </div>
            </div>
            {/* FAQ Item 6 */}
            <div className="glass-card bg-card/50 rounded-xl shadow-xl faq-item border border-white/10 backdrop-blur-lg hover:border-white/20 transition-all duration-300">
              <button className="w-full text-left p-6 focus:outline-none faq-question">
                <h3 className="text-xl font-semibold text-card-foreground">What basic features can I use when editing my clips?</h3>
              </button>
              <div className="px-6 pb-6 text-muted-foreground hidden faq-answer">
                <p>
                  With Clipoid's user-friendly editor, you can easily add text to your clips and precisely set their start and end points. You can also crop your clips for different social media platforms (e.g., 9:16 for TikTok). We are constantly adding new features!
                </p>
              </div>
            </div>
            {/* FAQ Item 7 */}
            <div className="glass-card bg-card/50 rounded-xl shadow-xl faq-item border border-white/10 backdrop-blur-lg hover:border-white/20 transition-all duration-300">
              <button className="w-full text-left p-6 focus:outline-none faq-question">
                <h3 className="text-xl font-semibold text-card-foreground">How is my data security and privacy ensured?</h3>
              </button>
              <div className="px-6 pb-6 text-muted-foreground hidden faq-answer">
                <p>
                  Your data security and privacy are of utmost importance to us. When you connect your Twitch account, we only request access to your VODs and use this data solely for creating clips for you. All your personal data is protected by industry-standard security protocols and encryption methods.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 text-primary-foreground relative"> {/* z-20 kaldırıldı */}
        <div className="container mx-auto px-6">
          <button
            onClick={toggleAboutSection}
            className="w-full glass-button flex items-center justify-center text-center focus:outline-none mb-6 group p-4 rounded-xl border border-white/10 hover:border-white/20 backdrop-blur-md hover:bg-white/[0.02] transition-all duration-300"
            aria-expanded={isAboutOpen}
            aria-controls="about-us-content"
          >
            <h2 className="text-4xl font-bold text-primary-foreground group-hover:text-primary-foreground/80 transition-colors">Who Are We?</h2>
            {isAboutOpen ? <ChevronUp size={36} className="ml-3 text-primary-foreground group-hover:text-primary-foreground/80 transition-colors" /> : <ChevronDown size={36} className="ml-3 text-primary-foreground group-hover:text-primary-foreground/80 transition-colors" />}
          </button>
          {isAboutOpen && (
            <div id="about-us-content" className="text-center">
              <div className="glass-card bg-white/[0.02] p-8 rounded-xl border border-white/10 backdrop-blur-md">
                <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto">
                  We're a team of dedicated developers and avid content creators who know firsthand the challenges of repurposing stream content. Clipoid was born from our desire to build powerful, yet simple tools that empower streamers to grow their brand without sacrificing precious streaming time. We're building Clipoid for streamers, with streamers.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer was previously here, now removed and handled by the global Footer component */}
    </AuroraBackground>
  );
};

export default LandingPage;